import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    LinearProgress,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Divider,
    Tabs,
    Tab,
    Badge
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { fetchProjects } from 'store/slices/project';
import { useGetStorageAnalyticsQuery, useGetStorageMigrationStatusQuery, useGetLakehouseStatsQuery } from 'store/slices/apiSlice';
import { updateFilters, setPage } from 'store/slices/storage';
import StorageAnalytics from './components/StorageAnalytics';
import FileExplorer from './components/FileExplorer';
import CloudStorageIntegration from './components/CloudStorageIntegration';
import StorageSettings from './components/StorageSettings';
import MigrationDashboard from './components/MigrationDashboard';

// icons
import {
    IconFiles,
    IconUpload,
    IconRefresh,
    IconSearch,
    IconFolder,
    IconPlus,
    IconSettings,
    IconChartBar,
    IconDatabase,
    IconCloud,
    IconLayoutGrid,
    IconFileAnalytics,
    IconCloudUpload,
    IconCloudDownload
} from '@tabler/icons-react';

// assets
import FolderLogo from '../../assets/icons/folder_icon.png';

// ==============================|| STORAGE DASHBOARD ||============================== //

const StorageDashboard = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { projects } = useSelector((state) => state.project);
    const { data: analytics } = useGetStorageAnalyticsQuery(undefined, { skip: false });
    const { data: migrationStatus } = useGetStorageMigrationStatusQuery(undefined, { skip: false });
    const { data: lakehouseStats } = useGetLakehouseStatsQuery(undefined, { skip: false });
    const { user } = useSelector((state) => state.account);

    const [loading, setLoading] = useState(false);
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [filterType, setFilterType] = useState('all');
    const [tabValue, setTabValue] = useState(0);
    const [storageMetrics, setStorageMetrics] = useState({
        totalFiles: 0,
        totalSize: 0,
        totalProjects: 0,
        recentUploads: 0
    });

    // Map routes to tab indices
    const routeToTab = {
        '/storage/dashboard': 0,
        '/storage/files': 1,
        '/storage/upload': 1,
        '/storage/analytics': 2,
        '/storage/migration': 3,
        '/storage/cloud': 4,
        '/storage/settings': 5
    };

    useEffect(() => {
        // Set tab based on current route
        const currentTab = routeToTab[location.pathname] || 0;
        setTabValue(currentTab);

        // Auto-open upload dialog if on upload route
        if (location.pathname === '/storage/upload') {
            setShowUploadDialog(true);
        }
    }, [location.pathname]);

    useEffect(() => {
        dispatch(fetchProjects());
    }, [dispatch]);

    // Khi vào từ Project & Storage (workspace) với projectId trong state → lọc theo project và mở tab File Manager
    useEffect(() => {
        const projectIdFromState = location.state?.projectId;
        if (projectIdFromState) {
            dispatch(updateFilters({ projectId: projectIdFromState }));
            dispatch(setPage(1));
            setTabValue(1);
        }
    }, [location.state?.projectId, dispatch]);

    useEffect(() => {
        loadStorageMetrics();
    }, [analytics, projects]);

    const handleRefresh = () => {
        setLoading(true);
        dispatch(fetchProjects()).finally(() => setLoading(false));
    };

    const loadStorageMetrics = async () => {
        if (analytics) {
            const sizeInGB = (analytics.total_size / (1024 * 1024 * 1024)).toFixed(2);
            setStorageMetrics({
                totalFiles: analytics.total_files || 0,
                totalSize: parseFloat(sizeInGB),
                totalProjects: projects?.length || 0,
                recentUploads: analytics.recent_uploads_24h || 0
            });
        }
    };

    const handleProjectSelect = (project) => {
        setTabValue(1);
        const projectIdentifier = project.project_name || project.id;
        dispatch(updateFilters({ projectId: projectIdentifier }));
        dispatch(setPage(1));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const filteredProjects =
        (projects && Array.isArray(projects))
            ? projects.filter((project) => {
                const matchesSearch =
                    project.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    project.description?.toLowerCase().includes(searchTerm.toLowerCase());
                return matchesSearch;
            })
            : [];

    const sortedProjects = Array.isArray(filteredProjects) ? [...filteredProjects].sort((a, b) => {
        switch (sortBy) {
            case 'name':
                return a.project_name.localeCompare(b.project_name);
            case 'size':
                return (b.project_document_num || 0) - (a.project_document_num || 0);
            case 'date':
                return new Date(b.created_at) - new Date(a.created_at);
            default:
                return 0;
        }
    }) : [];



    return (
        <MainCard
            title={
                <Box display="flex" alignItems="center" gap={2}>
                    <IconCloud />
                    <Typography variant="h3">Storage Management</Typography>
                    <Chip
                        label={lakehouseStats?.endpoint ? 'Lakehouse Connected' : 'Lakehouse Offline'}
                        size="small"
                        icon={<IconCloud size={16} />}
                        sx={{
                            bgcolor: lakehouseStats?.endpoint ? theme.palette.success.light : theme.palette.error.light,
                            color: lakehouseStats?.endpoint ? theme.palette.success.dark : theme.palette.error.dark
                        }}
                    />
                    {migrationStatus?.is_migration_running && (
                        <Chip
                            label="Migration Running"
                            size="small"
                            icon={<IconCloudUpload size={16} />}
                            sx={{
                                bgcolor: theme.palette.warning.light,
                                color: theme.palette.warning.dark
                            }}
                        />
                    )}
                    <Badge badgeContent={storageMetrics.recentUploads} color="error">
                        <Chip label="New Files" size="small" sx={{ ml: 1 }} />
                    </Badge>
                </Box>
            }
            secondary={
                <Box display="flex" gap={1}>
                    <Button variant="outlined" startIcon={<IconCloudUpload />} size="small">
                        Bulk Upload
                    </Button>
                    <Button variant="outlined" startIcon={<IconCloudDownload />} size="small">
                        Export
                    </Button>
                    <Tooltip title="Refresh">
                        <IconButton onClick={handleRefresh} disabled={loading}>
                            <IconRefresh />
                        </IconButton>
                    </Tooltip>
                    <Button variant="contained" startIcon={<IconPlus />} onClick={() => setShowUploadDialog(true)}>
                        Upload Files
                    </Button>
                    {user?.is_superuser && (
                        <Button variant="outlined" startIcon={<IconChartBar />} onClick={() => navigate('/storage/admin')} size="small">
                            Admin View
                        </Button>
                    )}
                </Box>
            }
        >
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconFolder size={18} />
                                Projects
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconLayoutGrid size={18} />
                                File Explorer
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconFileAnalytics size={18} />
                                Analytics
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconCloudUpload size={18} />
                                Migration
                                {migrationStatus?.is_migration_running && (
                                    <Chip label="Running" size="small" color="warning" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} />
                                )}
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconCloud size={18} />
                                Cloud Storage
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconSettings size={18} />
                                Settings
                            </Box>
                        }
                    />
                </Tabs>
            </Box>

            {/* Tab Content */}
            {tabValue === 0 && (
                <>
                    {/* Filter Section */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder="Search projects..."
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ flexGrow: 1, maxWidth: 400 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Sort by</InputLabel>
                            <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)}>
                                <MenuItem value="name">Name</MenuItem>
                                <MenuItem value="size">File Count</MenuItem>
                                <MenuItem value="date">Created Date</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Filter</InputLabel>
                            <Select value={filterType} label="Filter" onChange={(e) => setFilterType(e.target.value)}>
                                <MenuItem value="all">All Projects</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Projects Grid */}
                    <Box 
                        sx={{ 
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: 'repeat(2, 1fr)',
                                lg: 'repeat(3, 1fr)'
                            },
                            gap: 3
                        }}
                    >
                        {sortedProjects.map((project, index) => (
                            <Box key={index}>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: theme.shadows[8],
                                            transition: 'all 0.3s ease-in-out'
                                        },
                                        border: '1px solid',
                                        borderColor: theme.palette.divider
                                    }}
                                    onClick={() => handleProjectSelect(project)}
                                >
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                                            <Box
                                                sx={{
                                                    backgroundColor: '#00C85320',
                                                    borderRadius: '12px',
                                                    p: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <img src={FolderLogo} alt="Folder" style={{ width: 32, height: 32 }} />
                                            </Box>
                                            <Box flexGrow={1}>
                                                <Typography variant="h5" gutterBottom>
                                                    {project.project_name}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {project.description || 'No description'}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Box>
                                                <Typography variant="h6" color="primary">
                                                    {project.project_document_num || 0}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Files
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="textSecondary">
                                                    Created: {new Date(project.created_at).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        ))}
                    </Box>

                    {sortedProjects.length === 0 && (
                        <Box textAlign="center" py={8}>
                            <IconFolder size={64} style={{ opacity: 0.3, marginBottom: 16 }} />
                            <Typography variant="h4" gutterBottom sx={{ opacity: 0.5 }}>
                                No projects found
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.5 }}>
                                {searchTerm ? 'Try adjusting your search terms' : 'Create your first project to get started'}
                            </Typography>
                        </Box>
                    )}
                </>
            )}

            {/* File Explorer Tab */}
            {tabValue === 1 && <FileExplorer />}

            {/* Analytics Tab */}
            {tabValue === 2 && <StorageAnalytics />}

            {/* Migration Tab */}
            {tabValue === 3 && <MigrationDashboard />}

            {/* Cloud Storage Tab */}
            {tabValue === 4 && <CloudStorageIntegration />}

            {/* Settings Tab */}
            {tabValue === 5 && <StorageSettings />}
        </MainCard>
    );
};

export default StorageDashboard;
